import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI } from '../../services/api';
import { useAuthStore, useUIStore } from '../../store';
import { useOrdersWebSocket, requestNotificationPermission } from '../../hooks/useWebSocket';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  LogOut,
  Volume2,
  VolumeX,
  RefreshCw,
  Clock,
  ChevronRight,
  Wine,
  X,
  Bell,
  BellOff,
  ReceiptText,
  Wallet,
  CreditCard,
  Gift,
  Eye
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { formatOrderNumber } from '../../lib/utils';
import StaffAvailability from '../../components/StaffAvailability';

const STATUS_CONFIG = {
  pending: { label: 'Nouvelles', color: 'bg-blue-500', borderColor: 'border-l-blue-500' },
  preparing: { label: 'En préparation', color: 'bg-amber-500', borderColor: 'border-l-amber-500' },
  ready: { label: 'Prêtes', color: 'bg-green-500', borderColor: 'border-l-green-500' },
  delivered: { label: 'Livrées', color: 'bg-gray-400', borderColor: 'border-l-gray-400' }
};

const ORDER_STATUS_LABELS = {
  pending: 'Nouvelle',
  preparing: 'En préparation',
  ready: 'Prête',
  delivered: 'Livrée'
};

const formatTime = (isoString) => {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "A l'instant";
  if (diffMins < 60) return `${diffMins} min`;
  return `${Math.floor(diffMins / 60)}h ${diffMins % 60}m`;
};

const getPaymentBadge = (order) => {
  const method = order.payment_method;
  const isPaid = order.payment_status === 'paid';
  const cashToCollect = !!order.cash_to_collect;

  if (method === 'free') {
    return {
      label: 'Commande gratuite',
      className: 'bg-violet-100 text-violet-700 border-violet-200',
      icon: Gift
    };
  }

  if (method === 'card') {
    return {
      label: isPaid ? 'CB payée' : 'CB en attente',
      className: isPaid ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200',
      icon: CreditCard
    };
  }

  if (cashToCollect) {
    return {
      label: 'Espèces à récupérer',
      className: 'bg-orange-100 text-orange-700 border-orange-200',
      icon: Wallet
    };
  }

  return {
    label: isPaid ? 'Espèces récupérées' : 'Espèces',
    className: isPaid ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-700 border-slate-200',
    icon: Wallet
  };
};

const OrderItemsList = ({ items, compact = false }) => {
  const safeItems = items || [];

  if (!safeItems.length) {
    return (
      <p className="text-xs text-gray-400">
        Aucun article
      </p>
    );
  }

  return (
    <div className={compact ? 'mt-2 space-y-1' : 'space-y-2'}>
      {safeItems.map((item, idx) => (
        <div
          key={`${item.name}-${idx}`}
          className={
            compact
              ? 'text-xs text-gray-500'
              : 'flex items-start justify-between gap-3 rounded-xl bg-white px-3 py-2 border border-gray-100'
          }
        >
          <div className="min-w-0">
            <p className={compact ? 'text-xs text-gray-600' : 'font-medium text-gray-900 text-sm'}>
              <span className="font-mono text-gray-400">{item.quantity}x</span> {item.name}
            </p>

            {item.selected_options?.length > 0 && (
              <p className="text-xs text-purple-500 mt-0.5 leading-4">
                Options : {item.selected_options.join(', ')}
              </p>
            )}
          </div>

          {!compact && (
            <p className="font-semibold text-gray-900 whitespace-nowrap text-sm" style={{ fontFamily: 'JetBrains Mono' }}>
              {((item.price || 0) * (item.quantity || 0)).toFixed(2)}EUR
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

const TableOrdersDialog = ({
  open,
  onOpenChange,
  group,
  onStatusChange
}) => {
  if (!group) return null;

  const getNextStatus = (status) => {
    const flow = ['pending', 'preparing', 'ready', 'delivered'];
    const currentIndex = flow.indexOf(status);
    return currentIndex < flow.length - 1 ? flow[currentIndex + 1] : null;
  };

  const getAdvanceLabel = (order) => {
    const nextStatus = getNextStatus(order.status);
    if (!nextStatus) return null;

    if (
      nextStatus === 'delivered' &&
      order.payment_method === 'cash' &&
      order.payment_status !== 'paid'
    ) {
      return 'Livrée + espèces récupérées';
    }

    return `Passer en ${ORDER_STATUS_LABELS[nextStatus]?.toLowerCase()}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-24px)] max-w-3xl p-0 overflow-hidden rounded-3xl">
        <div className="bg-white">
          <DialogHeader className="px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-gray-100">
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Détail des commandes - Table {group.tableNumber}
            </DialogTitle>
            <DialogDescription>
              {group.orderIds.length} commande{group.orderIds.length > 1 ? 's' : ''} en cours
            </DialogDescription>
          </DialogHeader>

          <div className="p-4 sm:p-6 space-y-4 max-h-[75vh] overflow-y-auto">
            {group.orders
              .slice()
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
              .map((order) => {
                const paymentBadge = getPaymentBadge(order);
                const PaymentIcon = paymentBadge.icon;
                const nextStatus = getNextStatus(order.status);

                return (
                  <div
                    key={order.id}
                    className="rounded-2xl border border-gray-200 bg-gray-50 p-3 sm:p-4 shadow-sm"
                    data-testid={`staff-group-order-${order.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <ReceiptText className="w-4 h-4 text-gray-400" />

                          <span className="font-semibold text-gray-900 text-sm sm:text-base" style={{ fontFamily: 'Manrope' }}>
                            Commande {formatOrderNumber(order)}
                          </span>

                          <Badge
                            className={
                              order.status === 'delivered'
                                ? 'bg-gray-500'
                                : order.status === 'ready'
                                  ? 'bg-green-500'
                                  : order.status === 'preparing'
                                    ? 'bg-amber-500'
                                    : 'bg-blue-500'
                            }
                          >
                            {ORDER_STATUS_LABELS[order.status] || order.status}
                          </Badge>

                          <Badge variant="outline" className={paymentBadge.className}>
                            <PaymentIcon className="w-3 h-3 mr-1" />
                            {paymentBadge.label}
                          </Badge>

                          <Badge variant="outline">
                            {order.payment_status === 'paid' ? 'Paiement : payé' : 'Paiement : en attente'}
                          </Badge>
                        </div>

                        <div className="mt-2 flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                          <span style={{ fontFamily: 'JetBrains Mono' }}>
                            {formatTime(order.created_at)}
                          </span>

                          <span>
                            Table {order.table_number}
                          </span>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-gray-900 text-sm sm:text-base" style={{ fontFamily: 'JetBrains Mono' }}>
                          {(order.total_price || 0).toFixed(2)}EUR
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 rounded-2xl border border-gray-100 bg-white/70 p-3">
                      {order.status !== 'delivered' && <Button variant="outline" onClick={() => onStatusChange(order.id, 'cancelled')}>Annuler avec un motif</Button>}
                      <h3 className="font-semibold text-gray-900 mb-2 text-sm" style={{ fontFamily: 'Manrope' }}>
                        Articles
                      </h3>

                      <OrderItemsList items={order.items} />
                    </div>

                    {nextStatus && (
                      <div className="mt-3 flex justify-end">
                        <Button
                          onClick={() => onStatusChange(order.id, nextStatus)}
                          size="sm"
                          className={`h-9 rounded-full bg-none px-4 text-sm ${STATUS_CONFIG[nextStatus]?.color} hover:opacity-90 text-white shadow-sm`}
                          data-testid={`staff-group-order-advance-${order.id}`}
                        >
                          {getAdvanceLabel(order)}
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const GroupedOrderCard = ({ group, onStatusChange, onOpenDetails, lateMinutes = 15, now = Date.now() }) => {
  const [updating, setUpdating] = useState(false);
  const config = STATUS_CONFIG[group.status] || STATUS_CONFIG.pending;

  const getNextStatus = () => {
    const flow = ['pending', 'preparing', 'ready', 'delivered'];
    const currentIndex = flow.indexOf(group.status);
    return currentIndex < flow.length - 1 ? flow[currentIndex + 1] : null;
  };

  const handleAdvanceAll = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const nextStatus = getNextStatus();
    if (!nextStatus) return;

    setUpdating(true);

    try {
      await Promise.all(group.orderIds.map((id) => onStatusChange(id, nextStatus)));
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenDetails = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onOpenDetails(group);
  };

  const stopDetailsPropagation = (event) => {
    event.stopPropagation();
  };

  const nextStatus = getNextStatus();
  const nextConfig = nextStatus ? STATUS_CONFIG[nextStatus] : null;
  const isMulti = group.orderIds.length > 1;
  const oldestTime = group.orders.reduce((oldest, o) => {
    const t = new Date(o.created_at);
    return t < oldest ? t : oldest;
  }, new Date());

  const visibleItems = group.mergedItems;
  const overdue = group.status !== 'delivered' && now - oldestTime.getTime() >= lateMinutes * 60000;
  const fresh = group.orders.some(order => now - Date.parse(order.created_at) < 120000);

  return (
    <motion.article
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`staff-order-card relative bg-white rounded-xl border-l-4 ${config.borderColor} shadow-sm overflow-hidden ${overdue ? 'ring-2 ring-rose-400' : ''}`}
      data-testid={`order-group-table-${group.tableNumber}-${group.groupKey}`}
    >
      <div className="w-full text-left p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <Badge className={`${config.color} text-white px-3 py-1 text-lg`}>
              Table {group.tableNumber}
            </Badge>
            {fresh && group.status === 'pending' && <Badge className="bg-blue-100 text-blue-800">Nouveau</Badge>}
            {overdue && <Badge className="bg-rose-100 text-rose-800">Retard · {lateMinutes} min+</Badge>}

            <Badge variant="outline" className={group.paymentMethod === 'card' ? 'bg-emerald-50 text-emerald-700' : 'bg-orange-50 text-orange-700'}>
              {group.paymentMethod === 'card' ? (
                <>
                  <CreditCard className="w-3 h-3 mr-1" />
                  CB
                </>
              ) : (
                <>
                  <Wallet className="w-3 h-3 mr-1" />
                  {group.paymentMethod === 'free' ? 'Gratuite' : group.paymentMethod === 'mixed' ? 'Paiements mixtes' : 'Espèces'}
                </>
              )}
            </Badge>

            {group.cashToCollect && (
              <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-200">
                Espèces à récupérer
              </Badge>
            )}

            {isMulti && (
              <Badge variant="outline" className="text-xs font-mono">
                {group.orderIds.length} cmd
              </Badge>
            )}

            <span
              className="text-sm text-slate-600 flex items-center gap-1"
              style={{ fontFamily: 'JetBrains Mono' }}
            >
              <Clock className="w-3 h-3" />
              {formatTime(oldestTime.toISOString())}
            </span>
          </div>

          <span className="text-sm font-bold whitespace-nowrap" style={{ fontFamily: 'JetBrains Mono' }}>
            {group.totalPrice.toFixed(2)}EUR
          </span>
        </div>

        <div className="space-y-1 mt-2 overflow-hidden">
          {visibleItems.map((item, idx) => (
            <div key={idx} className="flex items-start gap-2 text-sm">
              <span className="font-mono text-gray-400 flex-shrink-0">{item.quantity}x</span>
              <div className="min-w-0">
                <span className="text-gray-700">{item.name}</span>
                {item.selected_options?.length > 0 && (
                  <p className="text-xs text-purple-500 leading-4">
                    {item.selected_options.join(', ')}
                  </p>
                )}
              </div>
            </div>
          ))}

        </div>

        {group.cashToCollect && <p className="mt-3 font-semibold text-orange-800">À encaisser : {group.orders.filter(o => o.cash_to_collect).reduce((sum, o) => sum + (o.total_price || 0), 0).toFixed(2)} €</p>}
      </div>

      <div
        className="px-3 pb-3"
        onClick={stopDetailsPropagation}
        onMouseDown={stopDetailsPropagation}
        onPointerDown={stopDetailsPropagation}
        onTouchStart={stopDetailsPropagation}
      >
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleOpenDetails}
          className="staff-detail-pill h-9 min-w-[92px] rounded-full px-3 active:scale-95 flex items-center justify-center gap-1.5 text-xs font-bold"
          data-testid={`order-group-details-table-${group.tableNumber}-${group.groupKey}`}
        >
          <Eye className="w-4 h-4" />
          Voir
        </Button>
      </div>

      <AnimatePresence initial={false}>
        {nextStatus && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden px-3 pb-3"
          >
            <Button
              onClick={handleAdvanceAll}
              disabled={updating}
              size="sm"
              className={`w-full bg-none ${nextConfig?.color} hover:opacity-90 text-white rounded-lg shadow-sm`}
              data-testid={`order-group-action-table-${group.tableNumber}-${group.groupKey}`}
            >
              {updating ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {nextStatus === 'delivered' && group.cashToCollect
                    ? 'Livrer et confirmer les espèces reçues'
                    : ({ preparing: 'Commencer', ready: 'Marquer prête', delivered: 'Livrer' })[nextStatus]}
                  {isMulti && ` (${group.orderIds.length})`}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
};

const NotifPrefsPanel = ({ open, onClose }) => {
  const { notifPrefs, setNotifPref, soundEnabled, toggleSound } = useUIStore();

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="absolute right-0 top-full mt-2 z-50 w-72 rounded-xl border border-gray-200 bg-white p-4 text-gray-900 shadow-xl"
      data-testid="notif-prefs-panel"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Manrope' }}>
          Notifications
        </h3>
        <button
          onClick={onClose}
          className="rounded-full p-1 hover:bg-gray-100"
          type="button"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-800">
            {soundEnabled ? (
              <Volume2 className="h-4 w-4 text-gray-500" />
            ) : (
              <VolumeX className="h-4 w-4 text-gray-400" />
            )}
            <span className="text-sm text-gray-900">Son activé</span>
          </div>
          <Switch
            checked={soundEnabled}
            onCheckedChange={toggleSound}
            data-testid="notif-sound-toggle"
          />
        </div>

        <div className="space-y-3 border-t border-gray-200 pt-3">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Recevoir une alerte quand :
          </p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-800">
              <div className="h-2.5 w-2.5 rounded-full bg-blue-500" />
              <span className="text-sm text-gray-900">Nouvelle commande</span>
            </div>
            <Switch
              checked={notifPrefs.pending}
              onCheckedChange={(v) => setNotifPref('pending', v)}
              data-testid="notif-pref-pending"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-800">
              <div className="h-2.5 w-2.5 rounded-full bg-amber-500" />
              <span className="text-sm text-gray-900">En préparation</span>
            </div>
            <Switch
              checked={notifPrefs.preparing}
              onCheckedChange={(v) => setNotifPref('preparing', v)}
              data-testid="notif-pref-preparing"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-800">
              <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
              <span className="text-sm text-gray-900">Prête à servir</span>
            </div>
            <Switch
              checked={notifPrefs.ready}
              onCheckedChange={(v) => setNotifPref('ready', v)}
              data-testid="notif-pref-ready"
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default function StaffDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [mobileStatus, setMobileStatus] = useState('pending');
  const [tableSearch, setTableSearch] = useState('');
  const [now, setNow] = useState(Date.now());
  const [lastSync, setLastSync] = useState(null);
  const [syncError, setSyncError] = useState(false);
  const [lateMinutes, setLateMinutes] = useState(() => {
    try { return Number(localStorage.getItem('glassorder-staff-delay')) || 15; } catch { return 15; }
  });
  const busyOrders = useRef(new Set());
  const { user, logout, isAuthenticated } = useAuthStore();
  const { soundEnabled } = useUIStore();

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/staff/login');
    } else {
      requestNotificationPermission();
    }
  }, [isAuthenticated, navigate]);

  const loadOrders = useCallback(async () => {
    try {
      const [response, delivered] = await Promise.all([ordersAPI.getActive(), ordersAPI.getAll({status:'delivered',limit:50})]);
      setOrders([...response.data, ...delivered.data]);
      setLastSync(Date.now());
      setSyncError(false);
    } catch (err) {
      setSyncError(true);
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 30000);
    return () => clearInterval(interval);
  }, [loadOrders]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15000);
    const refresh = () => { if (!document.hidden) loadOrders(); };
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { clearInterval(timer); window.removeEventListener('online', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [loadOrders]);

  const handleNewOrder = useCallback((order) => {
    setLastSync(Date.now());
    setOrders(prev => {
      const exists = prev.find(o => o.id === order.id);
      if (exists) return prev;
      return [order, ...prev];
    });
    toast.success(`Nouvelle commande - Table ${order.table_number}`);
  }, []);

  const handleOrderUpdated = useCallback((updatedOrder) => {
    setOrders(prev => [...prev.filter(o => o.id !== updatedOrder.id), updatedOrder]);
    setLastSync(Date.now());
  }, []);

  const connectionStatus = useOrdersWebSocket(user?.establishment_id, handleNewOrder, handleOrderUpdated);

  const handleStatusChange = async (orderId, newStatus, reason = '') => {
    if (busyOrders.current.has(orderId)) return;
    if (newStatus === 'cancelled' && !reason.trim()) {
      setCancelReason(''); setCancelTarget(orderId); return;
    }
    busyOrders.current.add(orderId);
    try {
      const { data } = await ordersAPI.updateStatus(orderId, newStatus, reason);
      if (data.undo_token) toast.success(`Commande mise à jour`, {
        duration: 15000,
        action: { label: 'Annuler l’action', onClick: async () => {
          try { await ordersAPI.undoStatus(orderId, data.undo_token); setSelectedGroup(null); await loadOrders(); toast.success('Action annulée'); }
          catch (err) { toast.error(err.response?.data?.detail || 'Annulation impossible'); await loadOrders(); }
        } }
      });

      setOrders(prev => prev.map(o =>
        o.id === orderId ? { ...o, status: newStatus } : o
      ));

      setSelectedGroup((prev) =>
        prev
          ? {
              ...prev,
              orders: prev.orders.map((o) =>
                o.id === orderId ? { ...o, status: newStatus } : o
              )
            }
          : prev
      );

      if (newStatus === 'delivered') {
        setTimeout(loadOrders, 16000);
      }
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur lors de la mise a jour');
      return false;
    } finally {
      busyOrders.current.delete(orderId);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/staff/login');
  };

  const handleOpenGroupDetails = (group) => {
    setSelectedGroup(group);
  };

  const handleCloseGroupDetails = (open) => {
    if (!open) {
      setSelectedGroup(null);
    }
  };

  const groupedByStatus = useMemo(() => {
    const result = {};
    ['pending', 'preparing', 'ready', 'delivered'].forEach(status => {
      const statusOrders = orders.filter(o => o.status === status && (!tableSearch.trim() || String(o.table_number || '').includes(tableSearch.trim())));
      const groups = {};

      statusOrders.forEach(order => {
        const paymentMethod = ['card', 'free'].includes(order.payment_method)
          ? order.payment_method
          : 'cash';
        const cashToCollect = !!order.cash_to_collect;
        const tableKey = order.table_number || order.table_id;
        const groupKey = `${tableKey}__${status}`;

        if (!groups[groupKey]) {
          groups[groupKey] = {
            groupKey,
            tableNumber: order.table_number || '?',
            status,
            paymentMethod,
            cashToCollect,
            orders: [],
            orderIds: [],
            mergedItems: [],
            totalPrice: 0
          };
        }

        const group = groups[groupKey];
        if (group.paymentMethod !== paymentMethod) group.paymentMethod = 'mixed';
        group.cashToCollect = group.cashToCollect || cashToCollect;
        group.orders.push(order);
        group.orderIds.push(order.id);
        group.totalPrice += order.total_price || 0;

        (order.items || []).forEach(item => {
          const existingItem = group.mergedItems.find(
            m => m.name === item.name &&
              JSON.stringify(m.selected_options || []) === JSON.stringify(item.selected_options || [])
          );

          if (existingItem) {
            existingItem.quantity += item.quantity;
          } else {
            group.mergedItems.push({ ...item });
          }
        });
      });

      result[status] = Object.values(groups).sort((a,b) => Math.min(...a.orders.map(o=>Date.parse(o.created_at))) - Math.min(...b.orders.map(o=>Date.parse(o.created_at))));
    });
    return result;
  }, [orders, tableSearch]);

  if (loading) {
    return (
      <div className="glassorder-staff-shell min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="glassorder-staff-shell min-h-screen">
      <Dialog open={!!cancelTarget} onOpenChange={open => { if (!open && !cancelling) setCancelTarget(null); }}>
        <DialogContent><DialogHeader><DialogTitle>Annuler cette commande</DialogTitle><DialogDescription>Le motif sera visible par le client. Les commandes déjà payées par carte nécessitent un remboursement préalable.</DialogDescription></DialogHeader>
          <label htmlFor="cancel-reason">Motif</label>
          <textarea id="cancel-reason" maxLength={300} className="min-h-24 rounded-xl border p-3" value={cancelReason} onChange={e => setCancelReason(e.target.value)} />
          <Button disabled={cancelling || !cancelReason.trim()} onClick={async () => { setCancelling(true); const ok = await handleStatusChange(cancelTarget, 'cancelled', cancelReason); setCancelling(false); if (ok) { setCancelTarget(null); setSelectedGroup(null); } }}>Confirmer l’annulation</Button>
          <Button variant="outline" disabled={cancelling} onClick={() => setCancelTarget(null)}>Conserver la commande</Button>
        </DialogContent>
      </Dialog>
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3">
        <StaffAvailability />
        <label className="flex items-center gap-2 text-sm">Alerte après
          <select aria-label="Seuil de retard" value={lateMinutes} className="min-h-11 rounded-xl border px-3" onChange={e=>{ const value=Number(e.target.value); setLateMinutes(value); try { localStorage.setItem('glassorder-staff-delay', String(value)); } catch {} }}>
            {[5,10,15,20,30,45,60].map(n=><option key={n} value={n}>{n} min</option>)}
          </select>
        </label>
      </div>
      <header className="staff-topbar px-3 sm:px-4 py-3 flex items-center justify-between gap-2 sticky top-0 z-20">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="staff-brand-icon h-10 w-10 shrink-0 rounded-2xl bg-white/95 flex items-center justify-center shadow-lg">
            <Wine className="w-5 h-5 text-[#7C3AED]" />
          </div>
          <div className="min-w-0">
            <h1 className="font-extrabold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>GlassOrder</h1>
            <p className="text-xs text-blue-100/75 truncate">Espace staff{user?.name ? ` - ${user.name}` : ''}</p>
          </div>
        </div>

        <div className="flex items-center gap-0.5 sm:gap-2 relative shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setNotifPanelOpen(!notifPanelOpen)}
            aria-label="Réglages des notifications"
            aria-expanded={notifPanelOpen}
            className="text-white hover:bg-white/10"
            data-testid="notif-settings-btn"
          >
            {soundEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
          </Button>

          <AnimatePresence>
            <NotifPrefsPanel open={notifPanelOpen} onClose={() => setNotifPanelOpen(false)} />
          </AnimatePresence>

          <Button
            variant="ghost"
            size="icon"
            onClick={loadOrders}
            aria-label="Actualiser les commandes"
            className="text-white hover:bg-white/10"
            data-testid="refresh-orders-btn"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            aria-label="Se déconnecter"
            className="text-white hover:bg-white/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <div className="space-y-3 p-3">
        <p role="status" className={`text-sm ${syncError || connectionStatus !== 'connected' ? 'text-amber-800' : 'text-emerald-800'}`}>
          {syncError ? 'Actualisation impossible — vérifiez la connexion' : connectionStatus === 'connected' ? '● Connecté en direct' : '● Reconnexion en cours · actualisation toutes les 30 s'}
          {lastSync && ` · Dernière mise à jour ${new Date(lastSync).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}`}
        </p>
        <label className="block text-sm font-medium">Rechercher une table
          <input type="search" inputMode="numeric" value={tableSearch} onChange={e=>setTableSearch(e.target.value)} placeholder="Numéro de table…" className="mt-1 min-h-11 w-full rounded-xl border bg-white p-3" />
        </label>
        <nav aria-label="Statut des commandes" className="grid grid-cols-2 gap-2 md:hidden">
          {Object.entries(STATUS_CONFIG).map(([status, config])=><button key={status} type="button" aria-pressed={mobileStatus===status} onClick={()=>setMobileStatus(status)} className={`min-h-12 rounded-xl border p-3 text-sm font-semibold ${mobileStatus===status ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'}`}>
            {config.label} ({(groupedByStatus[status] || []).reduce((sum,g)=>sum+g.orderIds.length,0)})
          </button>)}
        </nav>
      </div>

      <div className="staff-board p-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 xl:h-[calc(100dvh-68px)]">
        {['pending', 'preparing', 'ready', 'delivered'].map((status) => {
          const config = STATUS_CONFIG[status];
          const groups = groupedByStatus[status] || [];
          const orderCount = groups.reduce((sum, g) => sum + g.orderIds.length, 0);

          return (
            <div key={status} className={`staff-board-column flex-col bg-gray-50 rounded-lg overflow-hidden ${mobileStatus === status ? 'flex' : 'hidden md:flex'}`}>
              <div className={`${config.color} text-white px-3 py-2 flex items-center justify-between`}>
                <span className="font-semibold text-sm" style={{ fontFamily: 'Manrope' }}>
                  {config.label}
                </span>
                <Badge variant="secondary" className="bg-white/20 text-white text-xs">
                  {orderCount}
                </Badge>
              </div>

              <div className="staff-order-list min-h-0 flex-1 p-2 md:overflow-y-auto">
                <div className="space-y-2">
                  <AnimatePresence mode="popLayout">
                    {groups.map((group) => (
                      <GroupedOrderCard
                        key={group.groupKey}
                        group={group}
                        onStatusChange={handleStatusChange}
                        onOpenDetails={handleOpenGroupDetails}
                        lateMinutes={lateMinutes}
                        now={now}
                      />
                    ))}
                  </AnimatePresence>

                  {groups.length === 0 && (
                    <div className="text-center text-gray-400 text-sm py-8">
                      Aucune commande
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <TableOrdersDialog
        open={!!selectedGroup}
        onOpenChange={handleCloseGroupDetails}
        group={selectedGroup && { ...selectedGroup, orders: orders.filter(order => selectedGroup.orderIds.includes(order.id)) }}
        onStatusChange={handleStatusChange}
      />
    </div>
  );
}
